import { useState } from 'react';
import { ShieldAlert, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMyPenalties } from '@/hooks/penalty/useMyPenalties';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatMins(mins: number): string {
  if (mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

type PenaltyStatus = 'pending' | 'approved' | 'rejected';
type FilterStatus  = 'all' | PenaltyStatus;

const STATUS_FILTERS: { id: FilterStatus; label: string }[] = [
  { id: 'all',      label: 'All' },
  { id: 'approved', label: 'Approved' },
  { id: 'pending',  label: 'Pending' },
  { id: 'rejected', label: 'Rejected' },
];

const TYPE_LABELS: Record<string, string> = {
  late_arrival: 'Late Arrival',
  miss_punch:   'Miss Punch',
  manual:       'Manual',
};

const TYPE_COLORS: Record<string, string> = {
  late_arrival: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  miss_punch:   'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  manual:       'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

const STATUS_COLORS: Record<string, string> = {
  approved: 'text-emerald-600 dark:text-emerald-400',
  pending:  'text-amber-600 dark:text-amber-400',
  rejected: 'text-rose-500 dark:text-rose-400',
};

const STATUS_BORDER: Record<string, string> = {
  approved: '#10b981',
  pending:  '#f59e0b',
  rejected: '#ef4444',
};

// ── Component ─────────────────────────────────────────────────────────────────

interface MyPenaltiesTabProps {
  dateFrom: string;
  dateTo:   string;
}

export function MyPenaltiesTab({ dateFrom, dateTo }: MyPenaltiesTabProps) {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useMyPenalties({
    page,
    limit:     20,
    status:    filterStatus === 'all' ? undefined : filterStatus,
    date_from: dateFrom,
    date_to:   dateTo,
  });

  const penalties  = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => { setFilterStatus(f.id); setPage(1); }}
            className={`rounded-full px-3.5 py-1 text-xs font-semibold transition-colors border ${
              filterStatus === f.id
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white dark:bg-card border-slate-200 dark:border-border text-slate-600 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Loading skeletons */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 rounded-xl bg-slate-100 dark:bg-muted animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && penalties.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 dark:border-border bg-white/60 dark:bg-card/60 py-14 text-center">
          <ShieldAlert className="mb-2 h-8 w-8 text-slate-300 dark:text-slate-600" />
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">No penalties for this period</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
            {filterStatus !== 'all' ? `No ${filterStatus} penalties found.` : 'You have no penalties recorded.'}
          </p>
        </div>
      )}

      {/* Penalty cards */}
      {!isLoading && penalties.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {penalties.map((p) => (
            <div
              key={p.id}
              className="overflow-hidden rounded-xl border border-slate-200/80 dark:border-border bg-white dark:bg-card shadow-sm"
              style={{ borderLeft: `4px solid ${STATUS_BORDER[p.status] ?? '#94a3b8'}` }}
            >
              {/* Card header */}
              <div className="flex items-start justify-between px-4 pt-3 pb-2 border-b border-slate-100 dark:border-border/60">
                <div>
                  <p className="text-base font-bold text-slate-800 dark:text-slate-100">{formatDate(p.attendance_date)}</p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TYPE_COLORS[p.penalty_type] ?? ''}`}>
                      {TYPE_LABELS[p.penalty_type] ?? p.penalty_type}
                    </span>
                    {p.occurrence_number != null && p.penalty_type === 'late_arrival' && (
                      <span className="text-[11px] text-slate-400 dark:text-slate-500">
                        #{p.occurrence_number} late this month
                      </span>
                    )}
                  </div>
                </div>
                <span className={`text-xs font-semibold capitalize ${STATUS_COLORS[p.status] ?? ''}`}>
                  {p.status}
                </span>
              </div>

              {/* Card body */}
              <div className="space-y-2.5 px-4 py-3">
                {/* Deduction */}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">DEDUCTION</p>
                  {p.penalty_minutes > 0 ? (
                    <p className="text-sm font-bold text-rose-500 dark:text-rose-400">
                      -{formatMins(p.penalty_minutes)} deducted
                    </p>
                  ) : (
                    <p className="text-sm font-semibold text-slate-400 dark:text-slate-500">Warning only — no deduction</p>
                  )}
                </div>

                {/* Reason */}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">REASON</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-snug">
                    {p.reason || '—'}
                  </p>
                </div>

                {/* Rejection reason */}
                {p.status === 'rejected' && p.rejection_reason && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-400 mb-0.5">REJECTION REASON</p>
                    <p className="text-sm text-rose-500 dark:text-rose-400 leading-snug">{p.rejection_reason}</p>
                  </div>
                )}
              </div>

              {/* Card footer */}
              <div className="border-t border-slate-100 dark:border-border/60 px-4 py-2">
                <p className="text-[11px] text-slate-400 dark:text-slate-500">
                  {p.status === 'approved' && p.approved_at
                    ? `Approved ${new Date(p.approved_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                    : `Added ${new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                  }
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-slate-200 dark:border-border p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm text-slate-600 dark:text-slate-300">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
            className="rounded-lg border border-slate-200 dark:border-border p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
